<template>
    <VPane
        ref="el"
        class="splitpanes__pane--sidebar splitpanes__pane--shadow details-panel"
        :class="{
            'splitpanes__pane--fixed': $device.isMobile,
        }"
        max-size="80"
        min-size="35"
        :size="$device.isMobile ? 100 : panelWidth"
        v-if="visible"
    >
        <aside class="app-page app-page--no-shrink app-page--panel prevent-close">
            <template v-if="loading">
                <div class="details-panel__content details-panel__content--center details-panel__content--overlay">
                    <VLoader class="loader--mini loader--center"></VLoader>
                </div>
            </template>

            <template v-if="noData">
                <div class="details-panel__content details-panel__content--center details-panel__content--overlay">
                    <VIllustration :tip="t('no-data-' + random(0, 5))">
                        <NoDataSvg v-if="theme === 'light'"></NoDataSvg>
                        <NoDataDarkSvg v-else-if="theme === 'dark'"></NoDataDarkSvg>
                    </VIllustration>
                </div>
            </template>

            <component
                @before-unmount="resetOptions"
                @close="close"
                @configure="configure"
                @loading="loading = $event"
                @no-data="noData = $event"
                :is="panelComponent"
                v-show="!loading && !noData"
            ></component>
        </aside>
    </VPane>
</template>

<script setup lang="ts">
// Svg
import NoDataSvg from '@/assets/no-data.svg';
import NoDataDarkSvg from '@/assets/no-data-dark.svg';

// Components
import VLoader from '../components/VLoader.vue';
import VIllustration from '../components/VIllustration.vue';
import { Pane as VPane } from 'splitpanes';
import DetailsPanelTask from './DetailsPanelTask.vue';
import DetailsPanelNumeric from './DetailsPanelNumeric.vue';

// Other
import { hasParentWithClassName, isClickOnScrollbar } from '@/utils/dom-utils';
import { ref, onMounted, Component } from 'vue';

import Storages from '@/core/Storages';
import Settings from '@/core/Settings';
import { useRoute, useRouter } from 'vue-router';
import { computed, markRaw } from '@vue/reactivity';
import { onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const panels = {
    task: markRaw(DetailsPanelTask),
    numeric: markRaw(DetailsPanelNumeric),
};
const panelKeys = Object.keys(panels);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

let el = ref(null as HTMLDivElement | null);
let noData = ref(false);
let loading = ref(true);
let options = markRaw({} as Record<string, unknown>);

const panelWidth = computed(Storages.Settings.computedDebounce(Settings.UI.PanelWidth, 50));
const theme = computed(() => Storages.Settings.get(Settings.UI.Theme));

const panelComponent = computed((): Component | undefined => {
    const key = panelKeys.find((key) => !!route.query[key]) as keyof typeof panels | undefined;
    return key ? panels[key] : undefined;
});
const visible = computed((): boolean => !!panelComponent.value);

onMounted(() => {
    document.addEventListener('mousedown', onDocumentClick);
});

onBeforeUnmount((): void => {
    document.removeEventListener('mousedown', onDocumentClick);
});

function close(force = false): void {
    if (typeof options.close === 'function') {
        options.close(force);
        return;
    }

    router.replace({ query: {} });
}

function configure($options: Record<string, unknown>) {
    options = markRaw($options);
}

function onDocumentClick(event: MouseEvent): void {
    if (!visible.value) {
        return;
    }

    const element = event.target as HTMLElement;

    let stopCloseEvent = isClickOnScrollbar(event);
    if (stopCloseEvent) {
        return;
    }

    stopCloseEvent = hasParentWithClassName(element, ['prevent-close', 'splitpanes__splitter']);
    if (stopCloseEvent) {
        return;
    }

    close(true);
}

function resetOptions() {
    options = {};
}

function random(min: number, max: number) {
    return Math.round(Math.random() * (max - min) + min);
}
</script>

<style lang="scss">
.details-panel {
    &__content {
        display: flex;
        width: 100%;
        height: 100%;

        &--center {
            align-items: center;
            justify-content: center;
        }
    }
}
</style>
